import React, { useEffect, useState } from 'react';
import Dorsia from '../../assets/images/Dorsia.svg?react';
import styles from './Home.module.css';

/**
 * Real Dorsia restaurant imagery from dorsia.com — the most prestigious venues.
 * Curated across NYC, Miami, LA, London. Served from Dorsia's Framer CDN.
 */
const HERO_IMAGES = [
  // Carbone New York — iconic Italian, Major Food Group
  'https://framerusercontent.com/images/vuZLPj95GviWszpB1yQo3WJmI.jpg?width=1170&height=805',
  // Torrisi — ultra-hot, Michelin-starred
  'https://framerusercontent.com/images/5k1ZNxTqdPMQXbZoqeo008Q4TN0.jpg?width=1500&height=1001',
  // Restaurant Daniel — Daniel Boulud's flagship
  'https://framerusercontent.com/images/wX0fyDinNqCMmSRSFI3gE8kRs.jpg?width=1080&height=1080',
  // Le Pavillon — Daniel Boulud, 2 Michelin stars
  'https://framerusercontent.com/images/XHs2iu4i5915CH7cLOJ58PsSQ.jpg?width=2191&height=2922',
  // Cathédrale — stunning interior
  'https://framerusercontent.com/images/DYFny2xy39EOeTn6n4IzpO7o.jpg?width=1500&height=1875',
  // The Corner Store
  'https://framerusercontent.com/images/UYkZ9ptlx9zfB1yDOp9Ofeu20.jpg?width=1671&height=2412',
  // Craig's LA — celebrity hotspot
  'https://framerusercontent.com/images/2R4WZBy3J9kq9MVhgFV1mQWEIRM.jpg?width=1500&height=1000',
  // Delilah LA — glamorous supper club
  'https://framerusercontent.com/images/YRMZrztZNgjAeF6zJsrorCGg.jpg?width=4500&height=3000',
  // Carbone London
  'https://framerusercontent.com/images/i1CYTrAIL2ufJI4vqHo9cBl0Y60.jpg?width=2880&height=3600',
  // L'Atelier de Joël Robuchon Miami
  'https://framerusercontent.com/images/PDAHX7wPzzu3m58tKWiopXK80i8.jpg?width=1473&height=1920',
  // Gabriel Kreuther — 2 Michelin stars
  'https://framerusercontent.com/images/TgyoRhn29F0hAObsMPiIcLi9BCk.jpg?width=4160&height=5200',
  // The Twenty Two London — members' club
  'https://framerusercontent.com/images/1WKTT9HJbssWpgQEU30InW5Po.jpg?width=1500&height=2000',
];

/** Marquee images — more top-tier venues for the scrolling gallery. */
const MARQUEE_IMAGES = [
  // COTE Flatiron — Michelin-starred Korean steakhouse
  'https://framerusercontent.com/images/vrC0OgDk4h5ElfXFuF2ulxIZIas.jpg?width=4500&height=6000',
  // Marea — Michelin-starred seafood
  'https://framerusercontent.com/images/4rA3vVgyEvgNPI7E3nsLv2LsIwg.jpg?width=1000&height=1500',
  // Cosme — Enrique Olvera, World's 50 Best
  'https://framerusercontent.com/images/20ocHfwKpGyhuDPkkpZYLxm4ZRI.jpg?width=1170&height=1560',
  // Crown Shy — striking fine dining
  'https://framerusercontent.com/images/S5WH2aoYzwVHe1V9dAAveq7pio.jpg?width=1280&height=956',
  // Theodora — gorgeous space
  'https://framerusercontent.com/images/MWrufuKbPxlYtDy2UEkTXBAk.jpg?width=2500&height=1875',
  // Scarpetta — upscale Italian
  'https://framerusercontent.com/images/CQm3uxXVEwJUKiR1KN11UiT8.jpg?width=1440&height=1778',
  // Gekkō Miami — David Grutman x Bad Bunny
  'https://framerusercontent.com/images/uYBGq6EzDTXBrNhWTAHmegpXPU.jpg?width=3000&height=2001',
  // Chateau ZZ's Miami — hidden speakeasy
  'https://framerusercontent.com/images/uNiAJLXp4kzpQQtBhYllSWb2xU.jpg?width=4096&height=2732',
  // Mother Wolf LA — Evan Funke
  'https://framerusercontent.com/images/5BmXgv6L95hcFk7IjPIrk8dz5o.jpg?width=3600&height=2400',
  // Mount St. Restaurant London
  'https://framerusercontent.com/images/WiirdV7MnWDwCTsw6OV6vnurjk.jpg?width=3600&height=3442',
  // Zuma London — globally iconic
  'https://framerusercontent.com/images/Aj5eIOx27mdez7yKhz4mMYHpKg.jpg?width=3740&height=5545',
  // The Nice Guy LA — A-list hotspot
  'https://framerusercontent.com/images/h0yHqLOp9xnrov4yeCOqzfDj8.jpg?width=720&height=1080',
  // Casa Cruz NYC — stunning townhouse
  'https://framerusercontent.com/images/Kc5k4tVrC0W7CXZHnJ6TdOXFmwc.jpg?width=2000&height=3000',
  // Nobu Portman Square London
  'https://framerusercontent.com/images/7flFh9l4A25hGY0N7Vi303FDR8.jpg?width=1500&height=1000',
];

const PILLARS = [
  {
    title: 'Exclusive Access',
    description:
      'Tables at the most sought-after restaurants, reserved for those who know.',
  },
  {
    title: 'Curated Experiences',
    description:
      'Handpicked events, chef collaborations, and one-of-a-kind dining moments.',
  },
  {
    title: 'Effortless Booking',
    description:
      'From invitation to confirmation in seconds. No calls, no waiting.',
  },
];

const Home: React.FC = () => {
  const [scrollY, setScrollY] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className={styles.page}>
      {/* ── Hero Section ── */}
      <section className={styles.hero}>
        {/* Ambient image grid behind hero */}
        <div className={styles.imageGrid}>
          {HERO_IMAGES.map((src, i) => (
            <div
              key={i}
              className={styles.imageCell}
              style={{
                animationDelay: `${i * 0.15}s`,
                transform: `translateY(${scrollY * (0.02 + (i % 3) * 0.01)}px)`,
              }}
            >
              <img
                src={src}
                alt=""
                loading={i < 6 ? 'eager' : 'lazy'}
                draggable={false}
              />
            </div>
          ))}
        </div>

        {/* Dark overlay gradient */}
        <div className={styles.heroOverlay} />

        {/* Hero content */}
        <div
          className={`${styles.heroContent} ${loaded ? styles.heroContentVisible : ''}`}
        >
          <div className={styles.logoContainer}>
            <Dorsia className={styles.logo} />
          </div>

          <div className={styles.heroTextGroup}>
            <p className={styles.tagline}>Where dining becomes an invitation.</p>
            <div className={styles.dividerLine} />
            <p className={styles.subtitle}>
              Dorsia connects you to the world&apos;s most extraordinary restaurants
              and dining experiences through a members-only platform built on
              trust, taste, and unmatched access.
            </p>
          </div>

          <div className={styles.scrollIndicator}>
            <div className={styles.scrollDot} />
          </div>
        </div>
      </section>

      {/* ── Pillars Section ── */}
      <section className={styles.pillars}>
        <h2 className={styles.pillarsTitle}>The Dorsia Difference</h2>
        <div className={styles.pillarGrid}>
          {PILLARS.map((pillar) => (
            <div key={pillar.title} className={styles.pillarCard}>
              <h3 className={styles.pillarName}>{pillar.title}</h3>
              <p className={styles.pillarDescription}>{pillar.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Marquee Gallery ── */}
      <section className={styles.gallery}>
        <div className={styles.marqueeTrack}>
          <div className={styles.marqueeSlide}>
            {[...MARQUEE_IMAGES, ...MARQUEE_IMAGES].map((src, i) => (
              <div key={i} className={styles.marqueeImage}>
                <img src={src} alt="" loading="lazy" draggable={false} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Philosophy Section ── */}
      <section className={styles.philosophy}>
        <div className={styles.philosophyInner}>
          <h2 className={styles.philosophyTitle}>
            Reserved for those
            <br />
            who were invited.
          </h2>
          <p className={styles.philosophyBody}>
            Dorsia is not a public marketplace. Every booking begins with a personal
            invitation — a link shared directly with you by a trusted source.
            If you&apos;ve received one, you already belong.
          </p>
          <p className={styles.philosophyBody}>
            If you haven&apos;t, the best things come to those who are known.
          </p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className={styles.footer}>
        <Dorsia className={styles.footerLogo} />
        <p className={styles.footerText}>
          &copy; {new Date().getFullYear()} Dorsia Inc. All rights reserved.
        </p>
        <div className={styles.footerLinks}>
          <a href="https://dorsia.com" target="_blank" rel="noopener noreferrer">
            dorsia.com
          </a>
          <span className={styles.footerDot}>&middot;</span>
          <a
            href="https://apps.apple.com/app/dorsia-restaurant-reservations/id1538128040"
            target="_blank"
            rel="noopener noreferrer"
          >
            Download the App
          </a>
        </div>
      </footer>
    </div>
  );
};

export default Home;
